import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Eye, EyeOff, Loader2, LogOut, Save, X, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PostEditor({ post, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: post?.title || "",
    tag: post?.tag || "",
    excerpt: post?.excerpt || "",
    author: post?.author || "Rewat Raj",
    published: post?.published ?? true,
    content: post?.content || [{ type: "p", text: "" }],
  });
  const [saving, setSaving] = useState(false);

  const addBlock = (type) => setForm(f => ({ ...f, content: [...f.content, { type, text: "" }] }));
  const removeBlock = (idx) => setForm(f => ({ ...f, content: f.content.filter((_, i) => i !== idx) }));
  const updateBlock = (idx, text) => setForm(f => ({ ...f, content: f.content.map((b, i) => i === idx ? { ...b, text } : b) }));
  const moveBlock = (idx, dir) => {
    const newContent = [...form.content];
    const target = idx + dir;
    if (target < 0 || target >= newContent.length) return;
    [newContent[idx], newContent[target]] = [newContent[target], newContent[idx]];
    setForm(f => ({ ...f, content: newContent }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-white border border-[#ebe5dc] rounded-xl px-4 py-3 text-sm text-[#2d2926] placeholder-[#b8afa4] focus:border-[#2a9d8f] focus:ring-1 focus:ring-[#2a9d8f] transition-all outline-none";

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="blog-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-[#5c564e] mb-1.5">Title *</label>
          <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className={inputClass} placeholder="Post title" data-testid="editor-title" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#5c564e] mb-1.5">Tag / Category *</label>
          <input required value={form.tag} onChange={e => setForm({ ...form, tag: e.target.value })} className={inputClass} placeholder="e.g. AI Strategy" data-testid="editor-tag" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#5c564e] mb-1.5">Excerpt *</label>
        <textarea required value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} rows={2} className={`${inputClass} resize-none`} placeholder="Short summary for card display" data-testid="editor-excerpt" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-[#5c564e] mb-1.5">Author</label>
          <input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} className={inputClass} data-testid="editor-author" />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 rounded border-[#ebe5dc] text-[#2a9d8f] focus:ring-[#2a9d8f]" data-testid="editor-published" />
            <span className="text-sm text-[#5c564e]">{form.published ? "Published" : "Draft"}</span>
          </label>
        </div>
      </div>

      {/* Content blocks */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs font-semibold text-[#5c564e]">Content Blocks</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => addBlock("h")} className="text-xs bg-[#2a9d8f]/10 text-[#2a9d8f] px-3 py-1.5 rounded-full hover:bg-[#2a9d8f]/20 transition-all font-medium" data-testid="add-heading-block">+ Heading</button>
            <button type="button" onClick={() => addBlock("p")} className="text-xs bg-[#c4683c]/10 text-[#c4683c] px-3 py-1.5 rounded-full hover:bg-[#c4683c]/20 transition-all font-medium" data-testid="add-paragraph-block">+ Paragraph</button>
          </div>
        </div>
        <div className="space-y-3">
          {form.content.map((block, i) => (
            <div key={i} className="flex gap-2 items-start" data-testid={`content-block-${i}`}>
              <div className="flex flex-col gap-1 pt-2">
                <button type="button" onClick={() => moveBlock(i, -1)} className="text-[#b8afa4] hover:text-[#2a9d8f] transition-colors"><ChevronUp size={14} /></button>
                <button type="button" onClick={() => moveBlock(i, 1)} className="text-[#b8afa4] hover:text-[#2a9d8f] transition-colors"><ChevronDown size={14} /></button>
              </div>
              <span className={`mt-3 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${block.type === "h" ? "bg-[#2a9d8f]/10 text-[#2a9d8f]" : "bg-[#c4683c]/10 text-[#c4683c]"}`}>
                {block.type === "h" ? "H" : "P"}
              </span>
              {block.type === "h" ? (
                <input value={block.text} onChange={e => updateBlock(i, e.target.value)} className={`${inputClass} flex-1 font-semibold`} placeholder="Section heading" />
              ) : (
                <textarea value={block.text} onChange={e => updateBlock(i, e.target.value)} rows={3} className={`${inputClass} flex-1 resize-none`} placeholder="Paragraph text" />
              )}
              <button type="button" onClick={() => removeBlock(i)} className="mt-3 text-red-400 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-4 border-t border-[#ebe5dc]">
        <button type="submit" disabled={saving} data-testid="editor-save"
          className="bg-[#2a9d8f] text-white px-6 py-2.5 rounded-full text-sm font-bold hover:bg-[#238b7f] transition-all flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {post ? "Update Post" : "Publish Post"}
        </button>
        <button type="button" onClick={onCancel} className="text-[#5c564e] px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#ebe5dc]/50 transition-all flex items-center gap-2">
          <X size={14} /> Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminBlog() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchPosts = useCallback(async (tok) => {
    try {
      const res = await axios.get(`${API}/blog/posts?include_drafts=true`, { headers: { Authorization: `Bearer ${tok}` } });
      setPosts(res.data);
    } catch { }
    setLoading(false);
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem("admin_token");
    if (saved) {
      setToken(saved);
      fetchPosts(saved);
    } else {
      setLoading(false);
    }
  }, [fetchPosts]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      const tok = res.data.token;
      sessionStorage.setItem("admin_token", tok);
      setToken(tok);
      fetchPosts(tok);
    } catch {
      setLoginError("Invalid credentials");
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setToken(null);
    setPosts([]);
  };

  const headers = { Authorization: `Bearer ${token}` };

  const handleSave = async (formData) => {
    if (editingPost) {
      await axios.put(`${API}/blog/posts/${editingPost.slug}`, formData, { headers });
    } else {
      await axios.post(`${API}/blog/posts`, formData, { headers });
    }
    setEditingPost(null);
    setCreating(false);
    setLoading(true);
    fetchPosts(token);
  };

  const handleDelete = async (slug) => {
    await axios.delete(`${API}/blog/posts/${slug}`, { headers });
    setDeleteConfirm(null);
    setLoading(true);
    fetchPosts(token);
  };

  const handleTogglePublish = async (post) => {
    await axios.put(`${API}/blog/posts/${post.slug}`, { published: !post.published }, { headers });
    fetchPosts(token);
  };

  const inputClass = "w-full bg-white border border-[#ebe5dc] rounded-xl px-4 py-3 text-sm text-[#2d2926] placeholder-[#b8afa4] focus:border-[#2a9d8f] focus:ring-1 focus:ring-[#2a9d8f] transition-all outline-none";

  if (!token) {
    return (
      <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#2d2926]" style={{ fontFamily: 'Fraunces, serif' }}>Blog Admin</h1>
            <p className="text-sm text-[#8a8078] mt-1">Sign in to manage blog posts</p>
          </div>
          <form onSubmit={handleLogin} className="warm-card p-6 space-y-4" data-testid="admin-login-form">
            <input type="email" required placeholder="Email" value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} className={inputClass} data-testid="admin-email" />
            <input type="password" required placeholder="Password" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} className={inputClass} data-testid="admin-password" />
            {loginError && <p className="text-sm text-red-500">{loginError}</p>}
            <button type="submit" disabled={loginLoading} data-testid="admin-login-btn"
              className="w-full bg-[#2a9d8f] text-white py-3 rounded-full font-bold hover:bg-[#238b7f] transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              {loginLoading ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
            </button>
          </form>
          <div className="text-center mt-4">
            <Link to="/" className="text-sm text-[#2a9d8f] hover:text-[#238b7f] transition-colors flex items-center justify-center gap-1"><ArrowLeft size={14} /> Back to website</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <header className="sticky top-0 z-50 bg-[#faf7f2]/90 backdrop-blur-xl border-b border-[#ebe5dc]">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-[#5c564e] hover:text-[#2a9d8f] flex items-center gap-1"><ArrowLeft size={14} /> Site</Link>
            <span className="text-[#d5cec5]">/</span>
            <h1 className="text-lg font-bold text-[#2d2926]" style={{ fontFamily: 'Fraunces, serif' }}>Blog Manager</h1>
          </div>
          <div className="flex items-center gap-3">
            {!creating && !editingPost && (
              <button onClick={() => setCreating(true)} data-testid="new-post-btn"
                className="bg-[#2a9d8f] text-white px-5 py-2 rounded-full text-sm font-bold hover:bg-[#238b7f] transition-all flex items-center gap-1.5">
                <Plus size={14} /> New Post
              </button>
            )}
            <button onClick={handleLogout} className="text-[#5c564e] hover:text-red-500 transition-colors" data-testid="admin-logout-btn"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {(creating || editingPost) ? (
          <div>
            <h2 className="text-xl font-bold text-[#2d2926] mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
              {editingPost ? "Edit Post" : "New Post"}
            </h2>
            <PostEditor post={editingPost} onSave={handleSave} onCancel={() => { setCreating(false); setEditingPost(null); }} />
          </div>
        ) : loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#2a9d8f] animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#8a8078] mb-4">No blog posts yet</p>
            <button onClick={() => setCreating(true)} className="bg-[#2a9d8f] text-white px-6 py-2.5 rounded-full text-sm font-bold hover:bg-[#238b7f] transition-all">Create your first post</button>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <div key={post.id || post.slug} className="warm-card p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`admin-post-${post.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${post.published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {post.published ? "Published" : "Draft"}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#8a8078]">{post.tag}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-[#2d2926] truncate">{post.title}</h3>
                  <p className="text-xs text-[#8a8078] mt-0.5">{post.date} · {post.read_time} · By {post.author}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => handleTogglePublish(post)} title={post.published ? "Unpublish" : "Publish"} data-testid={`toggle-publish-${post.slug}`}
                    className="p-2 rounded-lg hover:bg-[#ebe5dc]/50 text-[#5c564e] transition-colors">
                    {post.published ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button onClick={() => setEditingPost(post)} data-testid={`edit-post-${post.slug}`}
                    className="p-2 rounded-lg hover:bg-[#ebe5dc]/50 text-[#5c564e] transition-colors">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => setDeleteConfirm(post.slug)} data-testid={`delete-post-${post.slug}`}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-6" onClick={() => setDeleteConfirm(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-[#2d2926] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>Delete this post?</h3>
              <p className="text-sm text-[#5c564e] mb-6">This action cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn"
                  className="bg-red-500 text-white px-5 py-2 rounded-full text-sm font-bold hover:bg-red-600 transition-all">Delete</button>
                <button onClick={() => setDeleteConfirm(null)} className="text-[#5c564e] px-5 py-2 rounded-full text-sm font-medium hover:bg-[#ebe5dc]/50 transition-all">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
